import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { projects } from '../data/mock';
import { MapPin, Zap, DollarSign } from 'lucide-react';
import SEO from '../components/SEO';

const Projects = () => {
  const [filter, setFilter] = useState('all');

  const categories = [
    { id: 'all', label: 'Todos' },
    { id: 'residencial', label: 'Residencial' },
    { id: 'comercial', label: 'Comercial' },
    { id: 'industrial', label: 'Industrial' }
  ];

  const getCategory = (title) => {
    if (title.includes('Residência') || title.includes('Condomínio')) return 'residencial';
    if (title.includes('Empresa') || title.includes('Comércio') || title.includes('Fazenda')) return 'comercial';
    if (title.includes('Indústria')) return 'industrial';
    return 'residencial';
  };

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => getCategory(project.title) === filter);

  return (
    <div className="min-h-screen pt-16">
      <SEO 
        title="Projetos Realizados - W3Solar | Portfólio de Instalações Solares"
        description="Confira nossos projetos de energia solar em São Paulo. Mais de 500 instalações realizadas com economia média de 95% na conta de luz."
        keywords="projetos energia solar, portfólio painéis solares, instalações solares são paulo, casos de sucesso energia solar"
      />
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Projetos Realizados
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Confira alguns dos nossos projetos de energia solar instalados em todo o Brasil
            </p>

            {/* Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <Badge
                  key={category.id}
                  onClick={() => setFilter(category.id)}
                  className={`cursor-pointer px-6 py-2 text-sm transition-all ${
                    filter === category.id
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.label}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="overflow-hidden hover:shadow-xl transition-all group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {project.title}
                    </h3>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <MapPin className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Localização</p>
                        <p className="font-medium text-gray-900">{project.location}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Zap className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Potência Instalada</p>
                        <p className="font-medium text-gray-900">{project.power}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <DollarSign className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Economia Mensal</p>
                        <p className="font-medium text-emerald-700">{project.economy}</p>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 pt-2 border-t">
                      {project.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Nenhum projeto encontrado nesta categoria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-5xl font-bold mb-2">500+</p>
              <p className="text-emerald-100">Projetos Concluídos</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">15 MW</p>
              <p className="text-emerald-100">Energia Instalada</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">95%</p>
              <p className="text-emerald-100">Satisfação dos Clientes</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">10 Anos</p>
              <p className="text-emerald-100">de Experiência</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
